import { SubscriptionBasedComponent } from 'src/app/subscription-helpers/subscription-based.component';
import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { Adjustment } from 'src/app/model/project/adjustment';
import { Project } from 'src/app/model/project/project';
import AdjustmentInvoice from 'src/app/model/invoice/adjustment-invoice';
import Company from 'src/app/model/company/company';
import { RequestService } from 'src/app/services/request.service';
import { SnackService } from 'src/app/services/snack.service';
import { LoadingService } from 'src/app/services/loading.service';
import InvoiceHelper from 'src/app/helpers/invoice-helper';
import { SubProject } from '../../../../model/project/sub-project';
import ProjectInvoice from 'src/app/model/invoice/project-invoice';
import { Subject } from 'rxjs';
import ProjectHelper from '../../../../helpers/project-helper';
import { StringHelper } from 'src/app/helpers/string-helper';
import { PriceLine } from 'src/app/model/price-line';
import * as globals from '../../../../../globals/globals';

@Component({
  selector: 'adjustment-invoicing',
  templateUrl: './adjustment-invoicing.component.html',
  styleUrls: ['./adjustment-invoicing.component.scss'],
})
export class AdjustmentInvoicingComponent
  extends SubscriptionBasedComponent
  implements OnInit
{
  @Input()
  public project: Project;
  @Input()
  public inputSubProject: SubProject;
  @Input()
  public invoiceSent: Subject<any>;
  @Input()
  public collapsed: boolean = true;
  @Input()
  public embeddedInQuotation: boolean = false;
  @Output()
  public outputInvoice = new EventEmitter<any>();

  inputAdjustment: Adjustment;

  projectCompanies: Company[] = [];
  invoices: ProjectInvoice[] = [];

  repoPriceLines = new Map<number, PriceLine[]>();

  init = false;
  safari = false;
  sendingInvoice = false;
  anySentToTeamleader = false;

  constructor(
    private requestService: RequestService,
    private snackService: SnackService,
    public loadingService: LoadingService
  ) {
    super();
  }

  ngOnInit() {
    this.inputAdjustment = <Adjustment>this.inputSubProject;
    this.safari = this.isSafari();
    this.projectCompanies = this.project.projectContacts.map(
      (pc) => pc.company
    );
    this.initLogic();
    if (this.embeddedInQuotation) {
      this.addSubscription(this.invoiceSent, () => {
        this.invoices = this.inputAdjustment.projectInvoices;
      });
    }
    this.init = true;
    this.anySentToTeamleader = InvoiceHelper.getAnySentToTeamleader(
      this.invoices
    );
  }

  getBaseProject() {
    return ProjectHelper.getBaseProject(this.project);
  }

  isSafari() {
    let ua = navigator.userAgent.toLowerCase();
    if (ua.indexOf('safari') != -1) {
      if (ua.indexOf('chrome') > -1) {
        return false; // Chrome
      } else {
        return true; // Safari
      }
    }
    return false;
  }

  initLogic() {
    let companyIds = new Set();
    if (this.inputAdjustment.projectInvoices == null) {
      this.inputAdjustment.projectInvoices = [];
    }
    for (let projectInvoice of this.inputAdjustment.projectInvoices) {
      companyIds.add(projectInvoice.company.id);
    }
    let invoiceAdded = false;
    for (let company of this.projectCompanies) {
      if (!companyIds.has(company.id)) {
        let projectInvoice = new AdjustmentInvoice(company);
        projectInvoice.priceLines = [];
        this.inputAdjustment.projectInvoices.push(projectInvoice);
        invoiceAdded = true;
        companyIds.add(company.id);
      }
    }
    this.invoices = this.inputAdjustment.projectInvoices;
    if (this.inputAdjustment.type === 'Repo') {
      for (let invoice of this.invoices) {
        this.repoPriceLines.set(
          invoice.company.id,
          InvoiceHelper.getRepoPriceLinesFromBaseProject(this.project, invoice)
        );
      }
    }
    if (invoiceAdded) {
      this.outputInvoice.emit();
    }
  }

  canInvoice() {
    if (!this.inputAdjustment.readyToInvoice) {
      return false;
    }
    return (
      !this.inputAdjustment.withPlacement ||
      (this.inputAdjustment.projectPlacement != null &&
        this.inputAdjustment.projectPlacement.placementDone)
    );
  }

  needsInvoiceChange(value) {
    this.inputAdjustment.needsInvoice = value;
    this.outputInvoice.emit();
  }

  extraInfoChange(value) {
    this.inputAdjustment.extraInfoInvoice = value;
    this.outputInvoice.emit();
  }

  readyToInvoiceChange() {
    this.outputInvoice.emit();
  }

  sendInvoice(sendToTeamleader: boolean) {
    this.sendingInvoice = true;
    this.requestService
      .post(
        { id: this.inputAdjustment.id, sendToTeamleader: sendToTeamleader },
        '/subprojects/invoiceProject/AdjustmentInvoice'
      )
      .subscribe((res) => {
        this.inputAdjustment.status = res.entity.status;
        this.inputAdjustment.projectInvoices = res.entity.projectInvoices;
        this.invoices = res.entity.projectInvoices;
        if (res.success) {
          this.anySentToTeamleader = true;
          if (this.invoiceSent != null) {
            this.invoiceSent.next();
          }
          this.snackService.showSuccess(
            sendToTeamleader
              ? res.message
              : 'Aanmaak factuur succesvol gesimuleerd'
          );
        } else {
          this.snackService.showError(res.message);
        }
        this.sendingInvoice = false;
      });
  }

  setPrices(list, index) {
    this.inputAdjustment.projectInvoices[index].priceLines = list;
    this.outputInvoice.emit();
  }

  getSendButtonLabel() {
    return InvoiceHelper.getSendButtonLabel(
      this.project.projectContacts == null
        ? 0
        : this.project.projectContacts.length,
      this.inputAdjustment.projectInvoices
    );
  }

  getLinkText(invoice: ProjectInvoice) {
    return InvoiceHelper.getLinkText(invoice);
  }

  referenceNumberChanged() {
    this.outputInvoice.emit();
  }

  getFormDisplayNameLowerCase() {
    return ProjectHelper.getFormDisplayNameLowerCase(this.inputSubProject);
  }

  displayPlacerFeedback() {
    return StringHelper.isEmpty(
      this.inputAdjustment.projectPlacement.placerFeedback
    )
      ? 'Geen informatie beschikaar'
      : this.inputAdjustment.projectPlacement.placerFeedback;
  }

  isTestEnvironment(): boolean {
    return !globals.production;
  }
}
